import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAdmin: false,
  loading: true,
  signIn: async () => ({ error: null }),
  signOut: async () => {},
});

const checkIsAdmin = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (error) {
      console.error("has_role error:", error);
      return false;
    }
    return !!data;
  } catch (e) {
    console.error("checkIsAdmin exception:", e);
    return false;
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // IMPORTANT: Set up listener BEFORE getSession per Supabase docs
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log("Auth event:", event);
        
        if (event === "SIGNED_OUT" || !newSession) {
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        setSession(newSession);
        setUser(newSession.user);
        
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") {
          // Use setTimeout to avoid Supabase deadlock on auth state change
          setTimeout(async () => {
            const admin = await checkIsAdmin(newSession.user.id);
            setIsAdmin(admin);
            setLoading(false);
          }, 0);
        } else {
          setLoading(false);
        }
      }
    );

    // Get initial session after listener is set up
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!initialSession) {
        // No session — clear everything and stop loading
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
      }
      // If there IS a session, onAuthStateChange INITIAL_SESSION will handle it
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error as Error };
    
    // Immediately set state so navigation can happen right away
    if (data.user && data.session) {
      setUser(data.user);
      setSession(data.session);
      const admin = await checkIsAdmin(data.user.id);
      setIsAdmin(admin);
    }
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    // Clear state first
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setLoading(false);
    
    // Sign out with global scope to invalidate ALL refresh tokens
    await supabase.auth.signOut({ scope: "global" });
    
    // Clear any leftover auth storage
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("sb-")) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
